import React from 'react';
import { Box, Typography } from '@mui/material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';

const getCategoryType = (item, isSelected) => {
  const allCategories = [
    ...(item.category || []),
    ...(item.category2 || []),
  ].map(c => (typeof c === 'string' ? c.toLowerCase() : ''));

  const nonVegVariants = [
    'non-veg', 'non veg', 'nonvegetarian', 'non vegetarian',
    'non-vegetarian', 'nonveg', 'egg', 'eggetarian',
    'meat', 'chicken', 'fish', 'seafood', 'mutton', 'prawn', 'lamb',
  ];
  const vegVariants = [
    'veg', 'vegetarian', 'pure veg', 'pure-veg', 'vegan',
  ];

  for (const cat of allCategories) {
    if (nonVegVariants.some(v => cat.includes(v))) {
      return {
        label: 'Non Veg',
        bgColor: isSelected ? 'rgba(255,255,255,0.2)' : '#FEE2E2',
        textColor: isSelected ? '#FFFFFF' : '#B91C1C',
      };
    }
    if (vegVariants.some(v => cat.includes(v))) {
      return {
        label: 'Veg',
        bgColor: isSelected ? 'rgba(255,255,255,0.2)' : '#DCFCE7',
        textColor: isSelected ? '#FFFFFF' : '#166534',
      };
    }
  }

  return {
    label: 'ITEM',
    bgColor: isSelected ? 'rgba(255,255,255,0.15)' : 'rgba(59,130,246,0.1)',
    textColor: isSelected ? 'rgba(255,255,255,0.8)' : '#2563EB',
  };
};

export const MenuItemCard = ({ item, quantity, onTap }) => {
  const isSelected = quantity > 0;
  const catType = getCategoryType(item, isSelected);

  return (
    <Box
      onClick={onTap}
      sx={{
        position: 'relative',
        borderRadius: '20px',
        cursor: 'pointer',
        userSelect: 'none',
        minHeight: 160,
        transition: 'transform 0.15s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease',
        '&:hover': {
          transform: 'scale(1.03)',
        },
        '&:active': {
          transform: 'scale(0.95)',
        },
      }}
    >
      {/* Main card body */}
      <Box
        sx={{
          borderRadius: '20px',
          height: '100%',
          minHeight: 160,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: isSelected
            ? 'linear-gradient(135deg, #7E57C2 0%, #FF4081 100%)'
            : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
          boxShadow: isSelected
            ? '0 6px 20px rgba(255, 64, 129, 0.3), 0 2px 6px rgba(126, 87, 194, 0.2)'
            : '0 2px 8px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: isSelected ? 'rgba(255,255,255,0.2)' : 'transparent',
          transition: 'background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
        }}
      >
        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            justifyContent: 'space-between',
          }}
        >
          {/* Top section: Category badge + Item name */}
          <Box>
            {/* Category badge */}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                px: 1,
                py: 0.4,
                borderRadius: '12px',
                fontSize: '0.6rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                bgcolor: catType.bgColor,
                color: catType.textColor,
                lineHeight: 1.4,
              }}
            >
              {catType.label}
            </Box>

            {/* Item name */}
            <Typography
              sx={{
                mt: 1,
                fontWeight: 600,
                fontSize: '0.88rem',
                color: isSelected ? '#FFFFFF' : '#1C1C1E',
                lineHeight: 1.3,
                letterSpacing: '0.2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {item.name}
            </Typography>
          </Box>

          {/* Bottom: Price pill aligned right */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                px: 1.5,
                py: 0.5,
                borderRadius: '14px',
                bgcolor: isSelected
                  ? 'rgba(255,255,255,0.15)'
                  : 'rgba(22,163,74,0.1)',
                border: '1px solid',
                borderColor: isSelected
                  ? 'rgba(255,255,255,0.3)'
                  : 'rgba(22,163,74,0.2)',
              }}
            >
              <CurrencyRupeeIcon
                sx={{
                  fontSize: '0.85rem',
                  color: isSelected ? '#FFFFFF' : '#16A34A',
                }}
              />
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  color: isSelected ? '#FFFFFF' : '#16A34A',
                  letterSpacing: '0.3px',
                  lineHeight: 1,
                }}
              >
                {item.price ?? '0'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Quantity badge — top right */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            top: -2,
            right: -2,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #4ADE80 0%, #14B8A6 100%)',
            borderRadius: '0 20px 0 16px',
            boxShadow: '0 3px 10px rgba(74, 222, 128, 0.4)',
            border: '2px solid #FFFFFF',
            zIndex: 2,
            transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            animation: 'badgePop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '@keyframes badgePop': {
              '0%': { transform: 'scale(0)' },
              '100%': { transform: 'scale(1)' },
            },
          }}
        >
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: '0.75rem',
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {quantity}
          </Typography>
        </Box>
      )}

      {/* Radial glow overlay when selected */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '20px',
            pointerEvents: 'none',
            background: 'radial-gradient(circle at top right, rgba(255,255,255,0.12) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
      )}
    </Box>
  );
};
